import { formatCurrency } from "@/lib/utils"
import { useEffect, useRef } from "react"
import type { Contrato } from "../schema"
import { Copiar, dataCurta } from "./Copiar"
import { FOCO } from "./foco"

const SELOS = {
    REPASSADO: { texto: "Repassado", cor: "bg-ok-wash text-ok" },
    NAO_IDENTIFICADO: { texto: "Não identificado", cor: "bg-off-wash text-off" },
    EM_ANALISE: { texto: "Em análise", cor: "bg-sunk text-ink ring-1 ring-line-strong" },
    ACEITA: { texto: "Repasse aceito", cor: "bg-ok-wash text-ok" },
    RECUSADA: { texto: "Alegação recusada", cor: "bg-stop-wash text-stop" },
}

// O que a QI identificou vale mais que a alegação.
const Selo = ({ contrato: { situacao, alegacao } }: { contrato: Contrato }) => {
    const selo = SELOS[situacao === "REPASSADO" || !alegacao ? situacao : alegacao]
    return <span className={`rounded px-2 py-0.5 text-xs ${selo.cor}`}>{selo.texto}</span>
}

const BoletoEmitido = ({ boleto }: { boleto: NonNullable<Contrato["boleto"]> }) => (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <span className="rounded bg-brand-wash px-2 py-0.5 text-xs text-ink">
            {boleto.linhaDigitavel ? "Boleto emitido" : "Gerando boleto"} · vence{" "}
            {dataCurta(boleto.vencimento)}
        </span>
        {boleto.linhaDigitavel && (
            <Copiar valor={boleto.linhaDigitavel} rotulo="Copiar a linha digitável do boleto" />
        )}
        {boleto.pdfUrl && (
            <a
                href={boleto.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-xs font-semibold text-brand underline ${FOCO}`}
            >
                PDF
            </a>
        )}
    </div>
)

const CaixaDeTodos = ({
    marcados,
    total,
    onAlternar,
}: { marcados: number; total: number; onAlternar: () => void }) => {
    const ref = useRef<HTMLInputElement>(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = marcados > 0 && marcados < total
    }, [marcados, total])
    return (
        <input
            ref={ref}
            type="checkbox"
            aria-label="Selecionar todas as parcelas"
            checked={total > 0 && marcados === total}
            disabled={total === 0}
            onChange={onAlternar}
            className={`size-4 accent-brand ${FOCO}`}
        />
    )
}

export interface Selecao {
    marcado: (contrato: Contrato) => boolean
    alternar: (contrato: Contrato) => void
    marcados: number
    selecionaveis: number
    alternarTodos: () => void
}

const COLUNAS = "sm:grid-cols-[minmax(0,1fr)_7rem_5.5rem_6.5rem_7rem]"

export const TabelaContratos = ({
    contratos,
    selecao,
}: { contratos: Contrato[]; selecao?: Selecao }) => (
    <div className="overflow-hidden rounded-lg border border-line">
        <div className="hidden items-center gap-3 bg-sunk px-4 py-2 sm:flex">
            {selecao && (
                <CaixaDeTodos
                    marcados={selecao.marcados}
                    total={selecao.selecionaveis}
                    onAlternar={selecao.alternarTodos}
                />
            )}
            <div
                className={`grid flex-1 gap-3 font-mono text-[0.64rem] uppercase tracking-[0.1em] text-faint ${COLUNAS}`}
            >
                <span>Colaborador</span>
                <span>Contrato</span>
                <span>Parcela</span>
                <span className="text-right">Valor</span>
                <span>Situação</span>
            </div>
        </div>
        {[...contratos]
            .sort((a, b) => a.colaborador.localeCompare(b.colaborador, "pt-BR"))
            .map((contrato) => (
                <div
                    key={contrato.id}
                    className="flex items-start gap-3 border-t border-line px-4 py-3 text-sm sm:items-center"
                >
                    {selecao && (
                        <input
                            type="checkbox"
                            aria-label={`Incluir ${contrato.colaborador}, contrato ${contrato.contrato}`}
                            checked={selecao.marcado(contrato)}
                            disabled={!contrato.cobravel}
                            onChange={() => selecao.alternar(contrato)}
                            className={`mt-1 size-4 accent-brand disabled:opacity-40 sm:mt-0 ${FOCO}`}
                        />
                    )}
                    <div
                        className={`grid flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 ${COLUNAS}`}
                    >
                        <div className="min-w-0">
                            <p className="truncate font-semibold">{contrato.colaborador}</p>
                            <p className="font-mono text-xs text-faint">
                                CPF {contrato.cpfMascarado}
                            </p>
                            {contrato.boleto && <BoletoEmitido boleto={contrato.boleto} />}
                        </div>
                        <span className="col-start-1 font-mono text-xs text-soft sm:col-auto sm:text-sm">
                            {contrato.contrato}
                        </span>
                        <span className="col-start-1 text-xs text-soft sm:col-auto sm:text-sm">
                            <span className="sm:hidden">Parcela </span>
                            {contrato.parcela
                                ? `${contrato.parcela.numero} de ${contrato.parcela.total}`
                                : "—"}
                        </span>
                        <span className="row-start-1 text-right font-semibold tabular-nums sm:row-auto">
                            {formatCurrency(contrato.valor)}
                        </span>
                        <span className="col-start-2 row-start-2 justify-self-end sm:col-auto sm:row-auto sm:justify-self-start">
                            <Selo contrato={contrato} />
                        </span>
                    </div>
                </div>
            ))}
    </div>
)
